<script setup lang="ts">
/**
 * UiKitSelect — кастомный селект HeroUI UI-kit (одиночный и множественный). Figma: 2943-31629.
 * Цвета только из токенов (классы hui-*). Клик-вне через onClickOutside (@vueuse).
 *
 * Слоты:
 *  - #leading           — иконка слева в триггере
 *  - #value             — кастомное содержимое триггера (заменяет чипы/плейсхолдер)
 *  - #option="{ option, selected, active }" — кастомный рендер строки в списке
 * Поиск: prop `searchable` добавляет поле фильтрации по label над списком.
 */
import { FOCUS_BORDER, type HuiColor, type HuiSize } from './palette'

interface SelectOption { label: string, value: string | number | null, disabled?: boolean, [key: string]: unknown }
type SelectVariant = 'flat' | 'bordered' | 'faded' | 'underlined'
type InputRadius = 'none' | 'sm' | 'md' | 'lg' | 'full'

const props = withDefaults(
  defineProps<{
    options: SelectOption[]
    color?: HuiColor
    variant?: SelectVariant
    size?: HuiSize
    radius?: InputRadius
    label?: string
    placeholder?: string
    description?: string
    errorMessage?: string
    multiple?: boolean
    disabled?: boolean
    invalid?: boolean
    searchable?: boolean
    searchPlaceholder?: string
  }>(),
  {
    color: 'primary',
    variant: 'flat',
    size: 'md',
    radius: 'md',
    label: '',
    placeholder: 'Выберите…',
    description: '',
    errorMessage: '',
    multiple: false,
    disabled: false,
    invalid: false,
    searchable: false,
    searchPlaceholder: 'Поиск…',
  },
)

const model = defineModel<string | number | (string | number)[] | null>({ default: null })

defineOptions({ name: 'UiKitSelect' })

const root = ref<HTMLElement | null>(null)
const dropdown = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
const open = ref(false)
const activeIndex = ref(-1)
const query = ref('')

const filteredOptions = computed<SelectOption[]>(() => {
  if (!props.searchable) return props.options
  const q = query.value.trim().toLowerCase()
  if (!q) return props.options
  return props.options.filter(o => String(o.label ?? '').toLowerCase().includes(q))
})

// Выпадающий список телепортируется в body с position:fixed, чтобы его не
// обрезали overflow-контейнеры (карточки, скролл-области, модалки).
const dropdownStyle = ref<Record<string, string>>({})

function updatePosition() {
  const r = root.value?.getBoundingClientRect()
  if (!r) return
  dropdownStyle.value = {
    position: 'fixed',
    top: `${r.bottom + 4}px`,
    left: `${r.left}px`,
    width: `${r.width}px`,
  }
}

watch(open, (isOpen) => {
  if (isOpen) {
    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    if (props.searchable) nextTick(() => searchInput.value?.focus())
  } else {
    query.value = ''
    window.removeEventListener('scroll', updatePosition, true)
    window.removeEventListener('resize', updatePosition)
  }
})

// При наборе запроса подсвечиваем первый доступный результат
watch(query, () => {
  activeIndex.value = filteredOptions.value.length ? 0 : -1
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updatePosition, true)
  window.removeEventListener('resize', updatePosition)
})

const selectedValues = computed<(string | number | null)[]>(() => {
  if (Array.isArray(model.value)) return model.value
  return model.value === null || model.value === undefined ? [] : [model.value]
})
const selectedOptions = computed(() => props.options.filter(o => selectedValues.value.includes(o.value)))
const selectedOption = computed(() => props.options.find(o => o.value === model.value) ?? null)
const isSelected = (opt: SelectOption) => selectedValues.value.includes(opt.value)

onClickOutside(root, () => { open.value = false }, { ignore: [dropdown] })

function toggle() {
  if (props.disabled) return
  open.value = !open.value
  if (open.value) activeIndex.value = filteredOptions.value.findIndex(isSelected)
}
function choose(opt: SelectOption) {
  if (opt.disabled) return
  if (props.multiple) {
    const cur = Array.isArray(model.value) ? [...model.value] : []
    const idx = cur.indexOf(opt.value as string | number)
    if (idx === -1) cur.push(opt.value as string | number)
    else cur.splice(idx, 1)
    model.value = cur
  } else {
    model.value = opt.value
    open.value = false
  }
}
function removeValue(val: string | number) {
  if (Array.isArray(model.value)) model.value = model.value.filter(v => v !== val)
}
function move(dir: 1 | -1) {
  if (!open.value) { open.value = true; return }
  const list = filteredOptions.value
  const n = list.length
  if (!n) { activeIndex.value = -1; return }
  let i = activeIndex.value
  for (let k = 0; k < n; k++) {
    i = (i + dir + n) % n
    if (!list[i]?.disabled) break
  }
  activeIndex.value = i
}
function enter() {
  if (open.value && activeIndex.value >= 0) choose(filteredOptions.value[activeIndex.value])
  else toggle()
}

const SIZE: Record<HuiSize, string> = {
  sm: 'min-h-9 text-sm px-3 py-1',
  md: 'min-h-11 text-base px-3.5 py-1.5',
  lg: 'min-h-12 text-base px-4 py-2',
}
const RADIUS: Record<InputRadius, string> = {
  none: 'rounded-none', sm: 'rounded-hui-sm', md: 'rounded-hui-md', lg: 'rounded-hui-lg', full: 'rounded-full',
}

const triggerClass = computed(() => {
  const c = props.color
  const inv = props.invalid
  const base = 'flex items-center gap-2 w-full box-border cursor-pointer transition-colors duration-150 outline-none text-left focus-visible:ring-2 focus-visible:ring-hui-focus'
  const radius = props.variant === 'underlined' ? 'rounded-none' : RADIUS[props.radius]
  const openFlat = open.value ? 'bg-hui-default-200' : ''
  const openBordered = open.value ? 'border-hui-primary' : ''
  const idleBordered = inv ? 'border-hui-danger' : `border-hui-default-200 hover:border-hui-default-400 ${FOCUS_BORDER[c]} ${openBordered}`
  const idleUnderlined = inv ? 'border-hui-danger' : `border-hui-default-300 hover:border-hui-default-400 ${FOCUS_BORDER[c]}`
  let variant: string
  switch (props.variant) {
    case 'flat': {
      variant = inv ? 'bg-hui-danger/10 border-2 border-transparent' : `bg-hui-default-100 border-2 border-transparent hover:bg-hui-default-200 ${openFlat}`
      break
    }
    case 'bordered': {
      variant = `bg-transparent border-2 ${idleBordered}`
      break
    }
    case 'faded': {
      variant = `bg-hui-default-100 border-2 ${inv ? 'border-hui-danger' : 'border-hui-default-200 hover:border-hui-default-400'}`
      break
    }
    case 'underlined': {
      variant = `bg-transparent border-b-2 ${idleUnderlined}`
      break
    }
    default: {
      variant = ''
    }
  }
  return [base, SIZE[props.size], radius, variant, props.disabled ? 'opacity-50 pointer-events-none' : '']
})

const labelClass = computed(() => [props.size === 'sm' ? 'text-xs' : 'text-sm', props.invalid ? 'text-hui-danger' : 'text-hui-foreground/70'])
</script>

<template>
  <div class="flex flex-col gap-1.5 w-full">
    <label v-if="label" class="font-medium leading-none" :class="labelClass">{{ label }}</label>

    <div ref="root" class="relative w-full">
      <div
        role="button"
        :tabindex="disabled ? -1 : 0"
        :class="triggerClass"
        :aria-expanded="open"
        aria-haspopup="listbox"
        @click="toggle"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="enter"
        @keydown.esc="open = false"
      >
        <!-- Ведущая иконка (опционально) -->
        <slot name="leading" />

        <div class="flex-1 min-w-0">
          <slot name="value" :selected-options="selectedOptions" :selected-values="selectedValues" :open="open">
            <!-- Множественный: чипы выбранного -->
            <div v-if="multiple" class="flex flex-wrap items-center gap-1">
              <template v-if="selectedOptions.length">
                <UiKitChip
                  v-for="o in selectedOptions"
                  :key="o.value"
                  size="sm"
                  :color="color"
                  variant="flat"
                  closable
                  @click.stop
                  @close="removeValue(o.value as string | number)"
                >
                  {{ o.label }}
                </UiKitChip>
              </template>
              <span v-else class="text-hui-foreground/40 truncate">{{ placeholder }}</span>
            </div>

            <!-- Одиночный -->
            <span v-else class="block truncate" :class="selectedOption ? 'text-hui-foreground' : 'text-hui-foreground/40'">
              {{ selectedOption?.label ?? placeholder }}
            </span>
          </slot>
        </div>

        <svg
          class="w-4 h-4 shrink-0 self-center text-hui-foreground/50 transition-transform duration-200"
          :class="{ 'rotate-180': open }"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M5 8l5 5 5-5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>

      <Teleport to="body">
        <Transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          leave-active-class="transition duration-100 ease-in"
          leave-to-class="opacity-0 -translate-y-1"
        >
          <div
            v-if="open"
            ref="dropdown"
            :style="dropdownStyle"
            class="z-50 flex flex-col overflow-hidden rounded-hui-md border border-hui-divider bg-hui-content1 shadow-lg outline-none"
          >
            <!-- Поиск -->
            <div v-if="searchable" class="p-1.5 border-b border-hui-divider">
              <div class="flex items-center gap-2 rounded-hui-sm bg-hui-default-100 px-2.5 py-1.5">
                <svg class="w-4 h-4 shrink-0 text-hui-foreground/40" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="9" cy="9" r="6" />
                  <path d="M14 14l4 4" stroke-linecap="round" />
                </svg>
                <input
                  ref="searchInput"
                  v-model="query"
                  type="text"
                  :placeholder="searchPlaceholder"
                  class="w-full bg-transparent text-sm text-hui-foreground placeholder:text-hui-foreground/40 outline-none"
                  @keydown.down.prevent="move(1)"
                  @keydown.up.prevent="move(-1)"
                  @keydown.enter.prevent="enter"
                  @keydown.esc="open = false"
                >
              </div>
            </div>

            <ul class="p-1 max-h-60 overflow-auto" role="listbox">
              <li
                v-for="(opt, i) in filteredOptions"
                :key="opt.value"
                role="option"
                :aria-selected="isSelected(opt)"
                class="flex items-center justify-between gap-2 px-2.5 py-2 text-sm rounded-hui-sm cursor-pointer transition-colors"
                :class="[
                  opt.disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-hui-default-100',
                  i === activeIndex && !opt.disabled ? 'bg-hui-default-100' : '',
                  isSelected(opt) ? 'text-hui-primary font-medium' : 'text-hui-foreground',
                ]"
                @click="choose(opt)"
                @mousemove="activeIndex = i"
              >
                <slot name="option" :option="opt" :selected="isSelected(opt)" :active="i === activeIndex">
                  <span class="truncate">{{ opt.label }}</span>
                  <svg v-if="isSelected(opt)" class="w-4 h-4 shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M4 10.5l4 4 8-9" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </slot>
              </li>
              <li v-if="!filteredOptions.length" class="px-2.5 py-2 text-sm text-hui-foreground/50">
                {{ searchable && query ? 'Ничего не найдено' : 'Нет вариантов' }}
              </li>
            </ul>
          </div>
        </Transition>
      </Teleport>
    </div>

    <p v-if="invalid && errorMessage" class="text-xs text-hui-danger">{{ errorMessage }}</p>
    <p v-else-if="description" class="text-xs text-hui-foreground/60">{{ description }}</p>
  </div>
</template>
